/**
 * Validation Utilities
 *
 * Pure functions for validating game entities. These ensure data integrity
 * before saving to the database and provide clear error messages.
 *
 * All validators return ValidationResult with:
 * - valid: boolean indicating if validation passed
 * - errors: array of error messages (empty if valid)
 */

import {
  IDEOLOGY_MIN,
  IDEOLOGY_MAX,
  IDEOLOGY_AXIS_COUNT,
  SALIENCE_MIN,
  SALIENCE_MAX,
} from '@/constants/ideology';

import type {
  Candidate,
  Cohort,
  Nation,
  District,
  CandidateRole,
  RecruitmentSource,
  AgeBracket,
  EducationLevel,
  IncomeLevel,
  SettlementType,
} from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface FormValidationResult {
  valid: boolean;
  fieldErrors: Record<string, string>;
}

// ============================================================================
// Constants
// ============================================================================

// Valid enum values for validation
export const VALID_AGE_BRACKETS: AgeBracket[] = ['youth', 'young_adult', 'middle_aged', 'senior'];
export const VALID_EDUCATION_LEVELS: EducationLevel[] = ['low', 'medium', 'high'];
export const VALID_INCOME_LEVELS: IncomeLevel[] = ['low', 'middle', 'high'];
export const VALID_SETTLEMENT_TYPES: SettlementType[] = ['rural', 'suburban', 'urban'];
export const VALID_CANDIDATE_ROLES: CandidateRole[] = [
  'none',
  'regional_candidate',
  'list_candidate',
  'party_leader',
  'minister',
  'chair',
];
export const VALID_RECRUITMENT_SOURCES: RecruitmentSource[] = [
  'youth_wing',
  'union',
  'business',
  'activist',
  'think_tank',
  'independent',
  'poached',
];

// ============================================================================
// Basic Validators
// ============================================================================

/**
 * Validate an ideology vector.
 * - Must be array of exactly 7 numbers
 * - Each value must be between -100 and +100
 * - No null/undefined values
 */
export function validateIdeologyVector(vector: unknown): ValidationResult {
  const errors: string[] = [];

  if (!Array.isArray(vector)) {
    return { valid: false, errors: ['Ideology vector must be an array'] };
  }

  if (vector.length !== IDEOLOGY_AXIS_COUNT) {
    errors.push(`Ideology vector must have exactly ${IDEOLOGY_AXIS_COUNT} elements, got ${vector.length}`);
  }

  for (let i = 0; i < vector.length; i++) {
    const val = vector[i];
    if (val === null || val === undefined) {
      errors.push(`Ideology axis ${i} cannot be null or undefined`);
    } else if (typeof val !== 'number') {
      errors.push(`Ideology axis ${i} must be a number, got ${typeof val}`);
    } else if (isNaN(val)) {
      errors.push(`Ideology axis ${i} cannot be NaN`);
    } else if (val < IDEOLOGY_MIN || val > IDEOLOGY_MAX) {
      errors.push(`Ideology axis ${i} must be between ${IDEOLOGY_MIN} and ${IDEOLOGY_MAX}, got ${val}`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate salience weights.
 * - Must be array of exactly 7 numbers
 * - Each value must be between 0 and 1
 * - No null/undefined values
 */
export function validateSalienceWeights(weights: unknown): ValidationResult {
  const errors: string[] = [];

  if (!Array.isArray(weights)) {
    return { valid: false, errors: ['Salience weights must be an array'] };
  }

  if (weights.length !== IDEOLOGY_AXIS_COUNT) {
    errors.push(`Salience weights must have exactly ${IDEOLOGY_AXIS_COUNT} elements, got ${weights.length}`);
  }

  for (let i = 0; i < weights.length; i++) {
    const val = weights[i];
    if (val === null || val === undefined) {
      errors.push(`Salience weight ${i} cannot be null or undefined`);
    } else if (typeof val !== 'number') {
      errors.push(`Salience weight ${i} must be a number, got ${typeof val}`);
    } else if (isNaN(val)) {
      errors.push(`Salience weight ${i} cannot be NaN`);
    } else if (val < SALIENCE_MIN || val > SALIENCE_MAX) {
      errors.push(`Salience weight ${i} must be between ${SALIENCE_MIN} and ${SALIENCE_MAX}, got ${val}`);
    }
  }

  return { valid: errors.length === 0, errors };
}

// ============================================================================
// Entity Validators
// ============================================================================

/**
 * Validate a cohort entity.
 */
export function validateCohort(cohort: unknown): ValidationResult {
  const errors: string[] = [];

  if (!cohort || typeof cohort !== 'object') {
    return { valid: false, errors: ['Cohort must be an object'] };
  }

  const c = cohort as Record<string, unknown>;

  // Validate ideology vector
  const ideologyResult = validateIdeologyVector(c.ideology_vector);
  if (!ideologyResult.valid) {
    errors.push(...ideologyResult.errors.map((e) => `ideology_vector: ${e}`));
  }

  // Validate salience weights
  const salienceResult = validateSalienceWeights(c.salience_weights);
  if (!salienceResult.valid) {
    errors.push(...salienceResult.errors.map((e) => `salience_weights: ${e}`));
  }

  // Validate population
  if (typeof c.population !== 'number' || c.population <= 0) {
    errors.push('Population must be a positive number');
  }

  // Validate base_turnout
  if (typeof c.base_turnout !== 'number' || c.base_turnout < 0 || c.base_turnout > 1) {
    errors.push('base_turnout must be between 0 and 1');
  }

  // Validate enums
  if (!VALID_AGE_BRACKETS.includes(c.age_bracket as AgeBracket)) {
    errors.push(`Invalid age_bracket: ${c.age_bracket}. Must be one of: ${VALID_AGE_BRACKETS.join(', ')}`);
  }

  if (!VALID_EDUCATION_LEVELS.includes(c.education_level as EducationLevel)) {
    errors.push(`Invalid education_level: ${c.education_level}. Must be one of: ${VALID_EDUCATION_LEVELS.join(', ')}`);
  }

  if (!VALID_INCOME_LEVELS.includes(c.income_level as IncomeLevel)) {
    errors.push(`Invalid income_level: ${c.income_level}. Must be one of: ${VALID_INCOME_LEVELS.join(', ')}`);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate a party entity.
 */
export function validateParty(party: unknown): ValidationResult {
  const errors: string[] = [];

  if (!party || typeof party !== 'object') {
    return { valid: false, errors: ['Party must be an object'] };
  }

  const p = party as Record<string, unknown>;

  // Validate ideology vector
  const ideologyResult = validateIdeologyVector(p.ideology_vector);
  if (!ideologyResult.valid) {
    errors.push(...ideologyResult.errors.map((e) => `ideology_vector: ${e}`));
  }

  // Validate name
  if (typeof p.name !== 'string' || p.name.trim().length === 0) {
    errors.push('Party name is required');
  } else if (p.name.trim().length < 1 || p.name.trim().length > 100) {
    errors.push('Party name must be between 1 and 100 characters');
  }

  // Validate abbreviation
  if (typeof p.abbreviation !== 'string' || p.abbreviation.trim().length === 0) {
    errors.push('Party abbreviation is required');
  } else if (p.abbreviation.trim().length < 1 || p.abbreviation.trim().length > 10) {
    errors.push('Party abbreviation must be between 1 and 10 characters');
  }

  // Validate color (hex format)
  if (typeof p.color !== 'string' || !/^#[0-9A-Fa-f]{6}$/.test(p.color)) {
    errors.push('Party color must be a valid hex code (e.g., #FF0000)');
  }

  // Validate money (if present)
  if (p.money !== undefined) {
    if (typeof p.money !== 'number' || p.money < 0) {
      errors.push('Party money must be a non-negative number');
    }
  }

  // Validate action_points (if present)
  if (p.action_points !== undefined) {
    if (typeof p.action_points !== 'number' || p.action_points < 0) {
      errors.push('Party action_points must be a non-negative number');
    }
  }

  // Validate prestige (if present)
  if (p.prestige !== undefined) {
    if (typeof p.prestige !== 'number' || p.prestige < 0 || p.prestige > 100) {
      errors.push('Party prestige must be between 0 and 100');
    }
  }

  // Validate momentum (if present)
  if (p.momentum !== undefined) {
    if (typeof p.momentum !== 'number' || p.momentum < -50 || p.momentum > 50) {
      errors.push('Party momentum must be between -50 and 50');
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate a candidate entity.
 */
export function validateCandidate(candidate: unknown): ValidationResult {
  const errors: string[] = [];

  if (!candidate || typeof candidate !== 'object') {
    return { valid: false, errors: ['Candidate must be an object'] };
  }

  const c = candidate as Record<string, unknown>;

  // Validate ideology vector
  const ideologyResult = validateIdeologyVector(c.ideology_vector);
  if (!ideologyResult.valid) {
    errors.push(...ideologyResult.errors.map((e) => `ideology_vector: ${e}`));
  }

  // Validate name
  if (typeof c.name !== 'string' || c.name.trim().length === 0) {
    errors.push('Candidate name is required');
  }

  // Validate age
  if (typeof c.age !== 'number' || c.age < 18 || c.age > 100) {
    errors.push('Candidate age must be between 18 and 100');
  }

  // Validate 0-100 range fields
  const rangeFields = [
    'loyalty',
    'recognition',
    'charisma',
    'admin_skill',
    'debate_skill',
    'scandal_prone',
    'ambition',
  ] as const;

  for (const field of rangeFields) {
    if (c[field] !== undefined) {
      const val = c[field];
      if (typeof val !== 'number' || val < 0 || val > 100) {
        errors.push(`${field} must be between 0 and 100`);
      }
    }
  }

  // Validate role enum (if present)
  if (c.role !== undefined && !VALID_CANDIDATE_ROLES.includes(c.role as CandidateRole)) {
    errors.push(`Invalid role: ${c.role}. Must be one of: ${VALID_CANDIDATE_ROLES.join(', ')}`);
  }

  // Validate recruitment_source enum (if present)
  if (c.recruitment_source !== undefined && !VALID_RECRUITMENT_SOURCES.includes(c.recruitment_source as RecruitmentSource)) {
    errors.push(`Invalid recruitment_source: ${c.recruitment_source}. Must be one of: ${VALID_RECRUITMENT_SOURCES.join(', ')}`);
  }

  return { valid: errors.length === 0, errors };
}

// ============================================================================
// Constraint Validators
// ============================================================================

/**
 * Validate that at most one candidate has the party_leader role.
 */
export function validatePartyLeaderConstraint(candidates: Candidate[]): ValidationResult {
  const leaders = candidates.filter((c) => c.role === 'party_leader' && c.is_active);

  if (leaders.length > 1) {
    const leaderNames = leaders.map((l) => l.name).join(', ');
    return {
      valid: false,
      errors: [`Multiple party leaders found: ${leaderNames}. Only one candidate can be party leader.`],
    };
  }

  return { valid: true, errors: [] };
}

/**
 * Validate that at most one candidate has the chair role.
 */
export function validatePartyChairConstraint(candidates: Candidate[]): ValidationResult {
  const chairs = candidates.filter((c) => c.role === 'chair' && c.is_active);

  if (chairs.length > 1) {
    const chairNames = chairs.map((c) => c.name).join(', ');
    return {
      valid: false,
      errors: [`Multiple chairs found: ${chairNames}. Only one candidate can be chair.`],
    };
  }

  return { valid: true, errors: [] };
}

/**
 * Validate that each district has at most one regional_candidate per party.
 */
export function validateDistrictCandidateConstraint(candidates: Candidate[]): ValidationResult {
  const errors: string[] = [];
  const districtMap = new Map<string, Candidate[]>();

  // Group regional candidates by district
  for (const candidate of candidates) {
    if (candidate.role === 'regional_candidate' && candidate.is_active && candidate.assigned_district_id) {
      const existing = districtMap.get(candidate.assigned_district_id) || [];
      existing.push(candidate);
      districtMap.set(candidate.assigned_district_id, existing);
    }
  }

  // Check for duplicates
  for (const [districtId, districtCandidates] of districtMap) {
    if (districtCandidates.length > 1) {
      const names = districtCandidates.map((c) => c.name).join(', ');
      errors.push(`Multiple regional candidates in district ${districtId}: ${names}`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate that party count doesn't exceed nation's max_parties.
 */
export function validateMaxPartiesConstraint(nation: Nation, partyCount: number): ValidationResult {
  if (partyCount > nation.max_parties) {
    return {
      valid: false,
      errors: [`Nation ${nation.name} has ${partyCount} parties but max_parties is ${nation.max_parties}`],
    };
  }

  return { valid: true, errors: [] };
}

/**
 * Validate that cohort populations sum correctly per district.
 * Flags discrepancies of more than 1% difference.
 */
export function validateCohortPopulationConsistency(
  _nation: Nation,
  cohorts: Cohort[],
  districts: District[]
): ValidationResult {
  const errors: string[] = [];
  const TOLERANCE_PERCENT = 0.01; // 1% tolerance

  // Group cohorts by district
  const cohortsByDistrict = new Map<string, Cohort[]>();
  for (const cohort of cohorts) {
    const existing = cohortsByDistrict.get(cohort.district_id) || [];
    existing.push(cohort);
    cohortsByDistrict.set(cohort.district_id, existing);
  }

  // Check each district
  for (const district of districts) {
    const districtCohorts = cohortsByDistrict.get(district.id) || [];
    const cohortPopulation = districtCohorts.reduce((sum, c) => sum + c.population, 0);

    if (district.population === 0 && cohortPopulation === 0) {
      continue; // Both zero is fine
    }

    if (district.population === 0) {
      errors.push(`District ${district.name} has population 0 but cohorts sum to ${cohortPopulation}`);
      continue;
    }

    const difference = Math.abs(cohortPopulation - district.population);
    const percentDiff = difference / district.population;

    if (percentDiff > TOLERANCE_PERCENT) {
      errors.push(
        `District ${district.name} population (${district.population}) differs from cohort sum (${cohortPopulation}) by ${(percentDiff * 100).toFixed(1)}%`
      );
    }
  }

  return { valid: errors.length === 0, errors };
}

// ============================================================================
// Form Validation Helpers
// ============================================================================

/**
 * Validate party creation form fields.
 * Returns field-level errors for UI display.
 */
export function validatePartyForm(data: {
  name: string;
  abbreviation: string;
  color: string;
  slogan?: string;
  ideology_vector: unknown;
}): FormValidationResult {
  const fieldErrors: Record<string, string> = {};

  // Name validation
  const trimmedName = data.name.trim();
  if (!trimmedName) {
    fieldErrors.name = 'Party name is required';
  } else if (trimmedName.length < 2) {
    fieldErrors.name = 'Party name must be at least 2 characters';
  } else if (trimmedName.length > 50) {
    fieldErrors.name = 'Party name must be at most 50 characters';
  }

  // Abbreviation validation
  const trimmedAbbrev = data.abbreviation.trim();
  if (!trimmedAbbrev) {
    fieldErrors.abbreviation = 'Abbreviation is required';
  } else if (trimmedAbbrev.length < 2) {
    fieldErrors.abbreviation = 'Abbreviation must be at least 2 characters';
  } else if (trimmedAbbrev.length > 6) {
    fieldErrors.abbreviation = 'Abbreviation must be at most 6 characters';
  }

  // Color validation
  if (!/^#[0-9A-Fa-f]{6}$/.test(data.color)) {
    fieldErrors.color = 'Please select a valid color';
  }

  // Ideology vector validation
  const ideologyResult = validateIdeologyVector(data.ideology_vector);
  if (!ideologyResult.valid) {
    fieldErrors.ideology_vector = ideologyResult.errors[0];
  }

  return {
    valid: Object.keys(fieldErrors).length === 0,
    fieldErrors,
  };
}

/**
 * Validate candidate form fields.
 * Returns field-level errors for UI display.
 */
export function validateCandidateForm(data: {
  name: string;
  age: number;
  ideology_vector: unknown;
  loyalty?: number;
  recognition?: number;
  charisma?: number;
  admin_skill?: number;
  debate_skill?: number;
  scandal_prone?: number;
  ambition?: number;
}): FormValidationResult {
  const fieldErrors: Record<string, string> = {};

  // Name validation
  const trimmedName = data.name.trim();
  if (!trimmedName) {
    fieldErrors.name = 'Candidate name is required';
  }

  // Age validation
  if (typeof data.age !== 'number' || isNaN(data.age)) {
    fieldErrors.age = 'Age is required';
  } else if (data.age < 18) {
    fieldErrors.age = 'Candidate must be at least 18 years old';
  } else if (data.age > 100) {
    fieldErrors.age = 'Candidate age cannot exceed 100';
  }

  // Ideology vector validation
  const ideologyResult = validateIdeologyVector(data.ideology_vector);
  if (!ideologyResult.valid) {
    fieldErrors.ideology_vector = ideologyResult.errors[0];
  }

  // Optional stat fields (0-100)
  const statFields = [
    { key: 'loyalty', label: 'Loyalty' },
    { key: 'recognition', label: 'Recognition' },
    { key: 'charisma', label: 'Charisma' },
    { key: 'admin_skill', label: 'Admin skill' },
    { key: 'debate_skill', label: 'Debate skill' },
    { key: 'scandal_prone', label: 'Scandal prone' },
    { key: 'ambition', label: 'Ambition' },
  ] as const;

  for (const { key, label } of statFields) {
    const value = data[key];
    if (value !== undefined) {
      if (typeof value !== 'number' || isNaN(value)) {
        fieldErrors[key] = `${label} must be a number`;
      } else if (value < 0 || value > 100) {
        fieldErrors[key] = `${label} must be between 0 and 100`;
      }
    }
  }

  return {
    valid: Object.keys(fieldErrors).length === 0,
    fieldErrors,
  };
}

/**
 * Validate single ideology axis value for real-time slider feedback.
 */
export function validateIdeologyAxisValue(value: number, axisIndex: number): string | null {
  if (typeof value !== 'number' || isNaN(value)) {
    return 'Value must be a number';
  }
  if (value < IDEOLOGY_MIN || value > IDEOLOGY_MAX) {
    return `Value must be between ${IDEOLOGY_MIN} and ${IDEOLOGY_MAX}`;
  }
  if (axisIndex < 0 || axisIndex >= IDEOLOGY_AXIS_COUNT) {
    return `Invalid axis index: ${axisIndex}`;
  }
  return null; // Valid
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Combine multiple validation results into one.
 */
export function combineValidationResults(...results: ValidationResult[]): ValidationResult {
  const allErrors = results.flatMap((r) => r.errors);
  return {
    valid: allErrors.length === 0,
    errors: allErrors,
  };
}

/**
 * Create a validation result from a single error.
 */
export function validationError(error: string): ValidationResult {
  return { valid: false, errors: [error] };
}

/**
 * Create a successful validation result.
 */
export function validationSuccess(): ValidationResult {
  return { valid: true, errors: [] };
}
